// ---------------------------------------------------------------------------
// Cell Renderers - Centralized Registry
// ---------------------------------------------------------------------------
// Reusable cell renderer components for DataTable columns.
// All components are pure functions following immutability patterns.
// ---------------------------------------------------------------------------

import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { PlatformId } from '@/components/v11/discovery/types';
import { buildProfileUrl, buildPostUrl } from '@/lib/socialUrlBuilder';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';

// ---------------------------------------------------------------------------
// Color Configuration Constants
// ---------------------------------------------------------------------------

/**
 * Platform brand colors - references CSS custom properties from index.css
 */
export const PLATFORM_CSS_VARS: Readonly<Record<PlatformId, string>> = {
  instagram: 'var(--platform-instagram)',
  x: 'var(--platform-x)',
  tiktok: 'var(--platform-tiktok)',
  youtube: 'var(--platform-youtube)',
} as const;

/**
 * Media type badge color mappings
 */
export const MEDIA_TYPE_COLORS: Readonly<Record<string, string>> = {
  Photo: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Reel: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  Carousel: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  Video: 'bg-red-500/10 text-red-400 border-red-500/20',
  Text: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  Slideshow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'Animated Gif': 'bg-green-500/10 text-green-400 border-green-500/20',
} as const;

/**
 * Team type badge color mappings (lowercase keys, normalized in component)
 */
export const TEAM_TYPE_COLORS: Readonly<Record<string, string>> = {
  team: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  player: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
} as const;

/**
 * Emotion badge color mappings
 * Grouped by sentiment: positive (green shades), negative (red/orange), neutral (gray/purple)
 */
export const EMOTION_COLORS: Readonly<Record<string, string>> = {
  // Positive emotions - Green shades
  Joy: 'bg-green-500/10 text-green-400 border-green-500/20',
  Excitement: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Admiration: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  Gratitude: 'bg-lime-500/10 text-lime-400 border-lime-500/20',
  Love: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  Pride: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  Relief: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  Hope: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  Solidarity: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Nostalgia: 'bg-violet-500/10 text-violet-400 border-violet-500/20',

  // Negative emotions - Red/Orange shades
  Sadness: 'bg-blue-600/10 text-blue-300 border-blue-600/20',
  Disappointment: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  Regret: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Embarrassment: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  Hatred: 'bg-red-600/10 text-red-400 border-red-600/20',
  Annoyance: 'bg-red-500/10 text-red-400 border-red-500/20',
  Frustration: 'bg-orange-600/10 text-orange-400 border-orange-600/20',
  Jealousy: 'bg-yellow-600/10 text-yellow-400 border-yellow-600/20',

  // Other emotions - Gray/Purple shades
  Fear: 'bg-purple-600/10 text-purple-400 border-purple-600/20',
  Irony: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20',
  Confusion: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  Surprise: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
} as const;

// ---------------------------------------------------------------------------
// Badge Components
// ---------------------------------------------------------------------------

/**
 * Platform badge with brand colors (same hex as TrendsPanel chips)
 * @param platform - Platform identifier (instagram, x, tiktok, youtube)
 */
export function PlatformBadge({ platform }: { readonly platform: string }) {
  const normalized = platform.toLowerCase() as PlatformId;
  const cssVar = PLATFORM_CSS_VARS[normalized];
  const pi = getPlatformIcon(platform);
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium capitalize"
      style={{ color: cssVar || undefined }}
    >
      {pi ? (
        <span className="flex-shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5" style={{ color: pi.color }}>
          {pi.icon}
        </span>
      ) : (
        <span
          className="h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: cssVar || 'currentColor' }}
        />
      )}
      {platform}
    </span>
  );
}

/**
 * Media type badge with color coding
 * @param mediaType - Media type string (Photo, Video, Reel, etc.)
 * @returns Badge or null if mediaType is null
 */
export function MediaTypeBadge({ mediaType }: { readonly mediaType: string | null }) {
  if (!mediaType) return null;
  const colorClass = MEDIA_TYPE_COLORS[mediaType] ?? '';
  return (
    <Badge variant="outline" className={`text-xs capitalize whitespace-nowrap ${colorClass}`}>
      {mediaType}
    </Badge>
  );
}

/**
 * Emotion badge with color coding based on sentiment
 * @param emotion - Emotion string (Joy, Sadness, Fear, etc.)
 * @returns Badge or null if emotion is null
 */
export function EmotionBadge({ emotion }: { readonly emotion: string | null }) {
  if (!emotion) return null;
  const colorClass = EMOTION_COLORS[emotion] ?? '';
  return (
    <Badge variant="outline" className={`text-xs capitalize whitespace-nowrap ${colorClass}`}>
      {emotion}
    </Badge>
  );
}

/**
 * Team type badge with color coding
 * @param teamType - Team type string (Team or Player)
 * @returns Badge or null if teamType is null
 */
export function TeamTypeBadge({ teamType }: { readonly teamType: string | null }) {
  if (!teamType) return null;
  const normalized = teamType.toLowerCase();
  const colorClass = TEAM_TYPE_COLORS[normalized] ?? '';
  return (
    <Badge variant="outline" className={`text-xs capitalize whitespace-nowrap ${colorClass}`}>
      {teamType}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Data Cell Components
// ---------------------------------------------------------------------------

/**
 * Sentiment score cell with color-coded value
 * @param value - Sentiment score (0-10 scale)
 * - Green (≥7): Positive sentiment
 * - Yellow (4-6): Neutral sentiment
 * - Red (<4): Negative sentiment
 * @returns Formatted score with color or dash for null
 */
export function SentimentCell({ value }: { readonly value: number | null | undefined }) {
  if (value == null) return <span className="text-v11-text-muted">—</span>;

  const color = value >= 7 ? 'text-green-400' : value >= 4 ? 'text-yellow-400' : 'text-red-400';

  return <span className={`font-medium ${color}`}>{value.toFixed(1)}</span>;
}

/**
 * Number cell with locale-aware formatting
 * Uses Intl.NumberFormat for proper thousand separators
 * @param value - Numeric value to format
 * @returns Formatted number or null for invalid values
 */
export function NumberCell({ value }: { readonly value: number | null | undefined }) {
  if (value == null || isNaN(value)) {
    return null;
  }
  return (
    <span className="text-v11-text-secondary tabular-nums">
      {new Intl.NumberFormat('en-US').format(value)}
    </span>
  );
}

/**
 * Compact number cell (e.g. 1.2M, 45.3K) for large values like followers
 */
export function CompactNumberCell({ value }: { readonly value: number | null | undefined }) {
  if (value == null || isNaN(value)) {
    return null;
  }
  const formatted = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
  return (
    <span
      className="text-v11-text-secondary tabular-nums font-medium"
      title={new Intl.NumberFormat('en-US').format(value)}
    >
      {formatted}
    </span>
  );
}

/**
 * Truncated text cell with ellipsis and tooltip
 * @param text - Text to display
 * @param maxLength - Maximum length before truncation (default: 60)
 * @returns Truncated text with full text in title attribute, or dash for null
 */
export function TruncatedText({
  text,
  maxLength = 60,
}: {
  readonly text: string | null;
  readonly maxLength?: number;
}) {
  if (!text) {
    return <span className="text-v11-text-muted">—</span>;
  }
  const truncated = text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  return (
    <span className="text-v11-text-secondary" title={text}>
      {truncated}
    </span>
  );
}

/**
 * Simple text cell with null handling
 * @param value - Text value to display
 * @returns Text or dash for null/empty values
 */
export function TextCell({ value }: { readonly value: string | null | undefined }) {
  if (!value || value === '—' || value === '' || value.toLowerCase() === 'undefined') {
    return <span className="text-v11-text-muted">—</span>;
  }
  return <span className="text-v11-text-secondary">{value}</span>;
}

// ---------------------------------------------------------------------------
// External Link Components
// ---------------------------------------------------------------------------

/**
 * External profile link - renders username as a clickable link to social profile
 * @param username - The username to display and link to
 * @param platform - The platform identifier (instagram, x, tiktok, youtube)
 * @returns Clickable link or plain text if platform not supported
 */
export function ExternalProfileLink({
  username,
  platform,
}: {
  readonly username: string | null | undefined;
  readonly platform: string | null | undefined;
}) {
  if (!username) {
    return <span className="text-v11-text-muted">—</span>;
  }

  const url = platform ? buildProfileUrl(platform, username) : null;

  if (!url) {
    return <span className="text-v11-text-secondary">{username}</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 max-w-[180px] text-v11-text-secondary hover:text-v11-accent hover:underline cursor-pointer"
      title={username}
      onClick={e => e.stopPropagation()}
    >
      <span className="truncate">{username}</span>
      <ExternalLink className="h-3 w-3 opacity-50 flex-shrink-0" />
    </a>
  );
}

/**
 * External post link - renders caption as a clickable link to the social post
 * @param caption - The caption text to display
 * @param id - The post ID (shortcode for Instagram, status ID for X, etc.)
 * @param author - The author username (required for X and TikTok)
 * @param platform - The platform identifier
 * @param maxLength - Maximum length before truncation (default: 60)
 * @returns Clickable truncated text with link or plain text if URL cannot be built
 */
export function ExternalPostLink({
  caption,
  id,
  author,
  platform,
  maxLength = 60,
}: {
  readonly caption: string | null | undefined;
  readonly id: string | null | undefined;
  readonly author: string | null | undefined;
  readonly platform: string | null | undefined;
  readonly maxLength?: number;
}) {
  if (!caption) {
    return <span className="text-v11-text-muted">—</span>;
  }

  const truncated = caption.length > maxLength ? `${caption.slice(0, maxLength)}...` : caption;
  const url = id && platform ? buildPostUrl(platform, id, author || undefined) : null;

  if (!url) {
    return (
      <span className="text-v11-text-secondary" title={caption}>
        {truncated}
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-start gap-1 text-v11-text-secondary hover:text-v11-accent hover:underline cursor-pointer"
      title={caption}
      onClick={e => e.stopPropagation()}
    >
      <span className="break-words whitespace-normal">{truncated}</span>
      <ExternalLink className="h-3 w-3 opacity-50 flex-shrink-0 mt-0.5" />
    </a>
  );
}

// ---------------------------------------------------------------------------
// Type Exports
// ---------------------------------------------------------------------------

/**
 * Available cell renderer types for column definitions
 */
export type CellRendererType =
  | 'text'
  | 'number'
  | 'compactNumber'
  | 'platform'
  | 'emotion'
  | 'mediaType'
  | 'teamType'
  | 'sentiment'
  | 'truncatedText'
  | 'externalProfileLink'
  | 'externalPostLink';

/**
 * Cell renderer component map
 * Used by column factory to resolve renderer types
 */
export const CELL_RENDERER_MAP = {
  text: TextCell,
  number: NumberCell,
  compactNumber: CompactNumberCell,
  platform: PlatformBadge,
  emotion: EmotionBadge,
  mediaType: MediaTypeBadge,
  teamType: TeamTypeBadge,
  sentiment: SentimentCell,
  truncatedText: TruncatedText,
  externalProfileLink: ExternalProfileLink,
  externalPostLink: ExternalPostLink,
} as const;
